<template>
  <a-modal
    :title="formTitle || '新增配置'"
    v-model:visible="open"
    @cancel="onClose"
    :width="800"
    :maskClosable="false"
    :keyboard="false"
    :destroyOnClose="true"
    :footer="null"
    class="create-form-modal"
  >
    <a-form
      ref="formRef"
      :model="form"
      :rules="rules"
      :label-col="{ flex: '0 0 150px' }"
      :wrapper-col="{ flex: '1 1 auto' }"
    >
      <a-form-item label="分组" name="configGroup">
        <a-input
          v-model:value="form.configGroup"
          :disabled="formType == 2"
          placeholder="请输入分组"
        />
      </a-form-item>
      <a-form-item label="变量名" name="configName">
        <a-input
          v-model:value="form.configName"
          :disabled="formType == 2"
          placeholder="请输入变量名"
        />
      </a-form-item>
      <a-form-item label="变量标题" name="title">
        <a-input v-model:value="form.title" placeholder="请输入变量标题" />
      </a-form-item>

      <a-form-item label="参数类型" name="type">
        <a-radio-group v-model:value="form.type" button-style="solid">
          <a-radio-button
            v-for="(d, index) in configTypeOptions"
            :key="index"
            :value="d.value"
          >
            {{ d.label }}
          </a-radio-button>
        </a-radio-group>
      </a-form-item>
      <a-form-item label="值" name="configValue" class="config-value-form-item">
        <a-textarea
          v-model:value="form.configValue"
          placeholder="请输入"
          v-if="form.type == 1"
        />
        <div class="editor-wrapper" v-if="form.type == 2">
          <editor
            id="configValueId"
            v-model:value="form.configValue"
          ></editor>
        </div>
        <a-input-number
          style="width: 100%"
          v-if="form.type == 3"
          v-model:value="form.configValue"
          placeholder="请输入值"
          :min="0"
        />
        <a-upload
          v-if="form.type == 4"
          listType="picture-card"
          class="avatar-uploader"
          :show-upload-list="false"
          :beforeUpload="(info) => fileBeforeUpload('configValue', info)"
          :customRequest="(info) => fileUploadRequest('configValue', info)"
          v-model:value="form.configValue"
        >
          <img
            v-if="form.type == 4 && form.configValue != null"
            :src="form.configValue"
            style="height: 104px; max-width: 300px"
          />
          <div v-else>
            <LoadingOutlined v-if="loading" />
            <PlusOutlined v-else />
            <div class="ant-upload-text">{{ loading ? '上传中' : '上传' }}</div>
          </div>
        </a-upload>
      </a-form-item>

      <a-form-item label="描述" name="description">
        <a-input v-model:value="form.description" placeholder="请输入描述" />
      </a-form-item>
      <div class="bottom-control">
        <a-space>
          <a-button @click="cancel" class="cancel-btn"> 取消 </a-button>
          <a-button type="primary" :loading="submitLoading" @click="submitForm" class="save-btn">
            保存
          </a-button>
        </a-space>
      </div>
    </a-form>
  </a-modal>
</template>

<script setup>
import { ref, reactive } from 'vue'
import { message } from 'ant-design-vue'
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons-vue'
import { getConfig, addConfig, updateConfig } from '@/api/app/config'
import Editor from '@/components/Editor'
import UploadFileToOSS from '@/utils/upload-file-to-oss'

const emit = defineEmits(['ok'])

const formRef = ref()
const submitLoading = ref(false)
const formTitle = ref('')
const formType = ref(1)
const open = ref(false)
const loading = ref(false)
const uploadLoading = ref(false)

const configTypeOptions = [
  { value: 1, label: '文本框' },
  { value: 2, label: '富文本' },
  { value: 3, label: '数字' },
  { value: 4, label: '图片' },
]

const form = reactive({
  id: null,
  configGroup: null,
  configName: null,
  title: null,
  description: null,
  configValue: null,
  configValueEn: null,
  type: 1,
  createTime: null,
  remark: null,
})

const rules = {
  configGroup: [
    { required: true, message: '变量名不能为空', trigger: 'blur' },
  ],
  configName: [
    { required: true, message: '分组不能为空', trigger: 'blur' },
  ],
  title: [
    { required: true, message: '变量标题不能为空', trigger: 'blur' },
  ],
  configValue: [
    { required: true, message: '值不能为空', trigger: 'blur' },
  ],
  type: [{ required: true, message: '类型不能为空', trigger: 'change' }],
}

function onClose() {
  open.value = false
}

function cancel() {
  open.value = false
  reset()
}

function reset() {
  formType.value = 1
  Object.assign(form, {
    id: null,
    configGroup: null,
    configName: null,
    title: null,
    description: null,
    configValue: null,
    configValueEn: null,
    type: 1,
    createTime: null,
    remark: null,
  })
}

function handleAdd(row) {
  console.log('CreateForm handleAdd 被调用', row)
  reset()
  formType.value = 1
  open.value = true
  formTitle.value = '添加'
  console.log('CreateForm open.value:', open.value)
}

function handleUpdate(row, ids) {
  reset()
  formType.value = 2
  const id = row ? row.id : ids
  getConfig({ id: id }).then((response) => {
    Object.assign(form, response.data)
    open.value = true
    formTitle.value = '修改'
  })
}

function fileBeforeUpload(field, fileInfo) {
  if (!fileInfo.type.includes('image')) {
    message.warning('请上传图片')
    return false
  }
  return true
}

function fileUploadRequest(field, fileInfo) {
  loading.value = true
  new UploadFileToOSS([fileInfo.file], {
    finally: (res) => {
      loading.value = false
      form.configValue = res[0].url
    },
  })
}

function beforeUploadEn(file) {
  const fileType = file.type
  if (fileType.indexOf('image') < 0) {
    message.warning('请上传图片')
    uploadLoading.value = false
    return false
  }
  form.configValueEn = ''
  //TODO 驗證文件大小
}

function handleChangeEn(info) {
  uploadLoading.value = true
  // Note: UploadFileToVOD is not imported, may need to be added if used
  // new UploadFileToVOD([info.file], {
  //   success: function (fileName) {
  //     form.configValueEn = fileName[0]
  //     uploadLoading.value = false
  //   },
  // })
  uploadLoading.value = false
}

async function submitForm() {
  try {
    await formRef.value.validate()
    submitLoading.value = true
    if (form.id !== undefined && form.id !== null) {
      await updateConfig(form)
      message.success('修改成功', 3)
      open.value = false
      emit('ok')
    } else {
      await addConfig(form)
      message.success('新增成功', 3)
      open.value = false
      emit('ok')
    }
  } catch (error) {
    return false
  } finally {
    submitLoading.value = false
  }
}

defineExpose({
  handleAdd,
  handleUpdate,
})
</script>

<style scoped>
/* 按钮样式 */
.save-btn {
  background-color: #165DFF !important;
  border-color: #165DFF !important;
  color: #FFFFFF !important;
  border-radius: 4px !important;
}

.save-btn:hover {
  background-color: #165DFF !important;
  border-color: #165DFF !important;
  color: #FFFFFF !important;
  opacity: 0.9;
}

.cancel-btn {
  background-color: #F3F3F3 !important;
  border-color: #F3F3F3 !important;
  color: #101010 !important;
  border-radius: 4px !important;
}

.cancel-btn:hover {
  background-color: #F3F3F3 !important;
  border-color: #F3F3F3 !important;
  color: #101010 !important;
}

.bottom-control {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  border-top: 1px solid #f0f0f0 !important;
}
</style>

<style>
/* Modal body 样式 - 使用全局样式确保生效 */
.create-form-modal .ant-modal-body {
  padding: 20px 16px 60px 24px !important;
  overflow-x: hidden !important;
  max-width: 100% !important;
}

/* 表单左边距调整 - 使用全局样式 */
.create-form-modal .ant-form {
  margin-left: 0 !important;
  margin-right: 0 !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
  max-width: 100% !important;
  overflow-x: hidden !important;
}

/* Label 文字左对齐，与输入框左边对齐 */
.create-form-modal .ant-form-item-label {
  padding-right: 0 !important;
  text-align: left !important;
}

.create-form-modal .ant-form-item-label > label {
  text-align: left !important;
  justify-content: flex-start !important;
  padding-left: 0 !important;
  margin-left: 0 !important;
}
</style>

<style>
/* 输入框样式 - 圆角和边框 */
.create-form-modal .ant-input,
.create-form-modal .ant-input:hover,
.create-form-modal .ant-input:focus {
  border-radius: 4px !important;
  border: 1px solid #C7C7C7 !important;
}

.create-form-modal .ant-input-affix-wrapper,
.create-form-modal .ant-input-affix-wrapper:hover,
.create-form-modal .ant-input-affix-wrapper-focused {
  border-radius: 4px !important;
  border: 1px solid #C7C7C7 !important;
}

/* 带前缀/后缀的输入框内部input去掉边框，避免嵌套 */
.create-form-modal .ant-input-affix-wrapper .ant-input {
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
}

/* 选择框样式 */
.create-form-modal .ant-select-selector,
.create-form-modal .ant-select-selector:hover,
.create-form-modal .ant-select-focused .ant-select-selector {
  border-radius: 4px !important;
  border: 1px solid #C7C7C7 !important;
}

/* 日期选择器样式 */
.create-form-modal .ant-picker,
.create-form-modal .ant-picker:hover,
.create-form-modal .ant-picker-focused {
  border-radius: 4px !important;
  border: 1px solid #C7C7C7 !important;
}

/* 数字输入框样式 */
.create-form-modal .ant-input-number,
.create-form-modal .ant-input-number:hover,
.create-form-modal .ant-input-number-focused {
  border-radius: 4px !important;
  border: 1px solid #C7C7C7 !important;
}

/* 文本域样式 */
.create-form-modal .ant-input[type="textarea"],
.create-form-modal .ant-input[type="textarea"]:hover,
.create-form-modal .ant-input[type="textarea"]:focus {
  border-radius: 4px !important;
  border: 1px solid #C7C7C7 !important;
}

/* 按钮保持圆角 */
.create-form-modal .ant-btn {
  border-radius: 4px !important;
}

/* 参数类型选择按钮样式 */
.create-form-modal .ant-radio-group {
  display: flex;
}

.create-form-modal .ant-radio-button-wrapper {
  border-radius: 4px !important;
  margin-right: 8px;
}

.create-form-modal .ant-radio-button-wrapper:first-child {
  border-radius: 4px !important;
}

.create-form-modal .ant-radio-button-wrapper:last-child {
  border-radius: 4px !important;
}

.create-form-modal .ant-radio-button-wrapper:not(:first-child)::before {
  display: none;
}

/* 选中状态的背景色和圆角 - 去掉边框 */
.create-form-modal .ant-radio-button-wrapper-checked {
  background-color: #165DFF !important;
  border: none !important;
  border-color: transparent !important;
  color: #FFFFFF !important;
  border-radius: 4px !important;
  box-shadow: none !important;
}

.create-form-modal .ant-radio-button-wrapper-checked:hover {
  background-color: #4080ff !important;
  border: none !important;
  border-color: transparent !important;
  color: #FFFFFF !important;
  box-shadow: none !important;
}

.create-form-modal .ant-radio-button-wrapper-checked:focus-within {
  background-color: #165DFF !important;
  border: none !important;
  border-color: transparent !important;
  color: #FFFFFF !important;
  box-shadow: none !important;
}

/* 富文本编辑器外层容器 - 防止超出 */
.create-form-modal .ant-form-item-control-input-content {
  width: 100% !important;
  max-width: 100% !important;
  overflow: hidden !important;
  box-sizing: border-box !important;
}

/* 富文本编辑器容器样式优化 - 与其他输入框完全一致 */
.create-form-modal .editor-wrapper {
  width: 100% !important;
  box-sizing: border-box !important;
}

/* 确保表单项不会超出 */
.create-form-modal .ant-form-item {
  margin-bottom: 24px !important;
  overflow: visible !important;
  width: 100% !important;
  max-width: 100% !important;
}

.create-form-modal .ant-form-item-control {
  max-width: 100% !important;
  overflow: hidden !important;
  width: 100% !important;
}
</style>

<style>
/* 富文本编辑器全局样式 - 防止超出 Modal（使用全局样式确保生效） */
.create-form-modal .editor-wrapper #editor,
.create-form-modal .editor-wrapper .editor {
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box !important;
  margin: 0 !important;
  padding: 0 !important;
}

/* wangeditor 主容器 - 确保宽度100% */
.create-form-modal .editor-wrapper > div {
  width: 100% !important;
  box-sizing: border-box !important;
}

/* wangeditor 工具栏 - 防止超出 */
.create-form-modal .editor-wrapper .w-e-toolbar,
.create-form-modal .editor-wrapper [class*="toolbar"] {
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box !important;
  overflow-x: auto !important;
  overflow-y: hidden !important;
  margin: 0 !important;
  padding: 0 !important;
}

/* wangeditor 文本容器 - 防止超出 */
.create-form-modal .editor-wrapper .w-e-text-container,
.create-form-modal .editor-wrapper .w-e-text-container-wrapper,
.create-form-modal .editor-wrapper [class*="text-container"] {
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box !important;
  margin: 0 !important;
  padding: 0 !important;
  overflow-x: auto !important;
  overflow-y: auto !important;
}

/* wangeditor 文本编辑区域 - 防止超出 */
.create-form-modal .editor-wrapper .w-e-text,
.create-form-modal .editor-wrapper [class*="w-e-text"] {
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box !important;
  overflow-x: auto !important;
  word-wrap: break-word !important;
  word-break: break-all !important;
  overflow-wrap: break-word !important;
  margin: 0 !important;
  padding: 8px !important;
}

/* 富文本编辑器表单项控制 - 只对富文本编辑器居中 */
.create-form-modal .ant-form-item-control-input-content {
  max-width: 100% !important;
}

.create-form-modal .ant-form-item-control {
  max-width: 100% !important;
}

/* 富文本编辑器表单项 - 确保与其他输入框对齐 */
.create-form-modal .config-value-form-item .ant-form-item-control-input-content {
  width: 100% !important;
}

/* wangeditor 所有内部元素使用 border-box */
.create-form-modal .editor-wrapper .w-e * {
  box-sizing: border-box !important;
}

/* 确保 Modal body 不会超出 */
.create-form-modal .ant-modal-body {
  overflow-x: hidden !important;
  max-width: 100% !important;
}

/* 确保表单不会超出 */
.create-form-modal .ant-form {
  max-width: 100% !important;
  overflow-x: hidden !important;
}

/* 确保富文本编辑器与 textarea 宽度完全一致 */
.create-form-modal .config-value-form-item .editor-wrapper {
  width: 100% !important;
}

.create-form-modal .config-value-form-item .editor-wrapper > div {
  width: 100% !important;
}

.create-form-modal .config-value-form-item .editor-wrapper #editor {
  width: 100% !important;
}

.create-form-modal .config-value-form-item .editor-wrapper .w-e {
  width: 100% !important;
}

.create-form-modal .config-value-form-item .editor-wrapper .w-e-toolbar {
  width: 100% !important;
}

.create-form-modal .config-value-form-item .editor-wrapper .w-e-text-container {
  width: 100% !important;
}
</style>
